import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Leaf, LogIn, Mail, User, Lock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminLogin = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { login, isAuthenticated } = useAuth();
  
  const [isSetupMode, setIsSetupMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checkingSetup, setCheckingSetup] = useState(true);
  
  const [loginData, setLoginData] = useState({
    username: '',
    password: ''
  });
  
  const [setupData, setSetupData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: ''
  });

  useEffect(() => {
    // Redirect if already authenticated
    if (isAuthenticated) {
      navigate('/admin');
      return;
    }
    
    checkSetupRequired();
  }, [isAuthenticated]);

  const checkSetupRequired = async () => {
    try {
      const response = await axios.get(`${API}/auth/check-setup`);
      setIsSetupMode(response.data.setup_required);
    } catch (error) {
      console.error('Failed to check setup:', error);
    } finally {
      setCheckingSetup(false);
    }
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    
    if (!loginData.username || !loginData.password) {
      toast({
        title: "Error",
        description: "Please fill in all fields",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    const result = await login(loginData.username, loginData.password);
    
    if (result.success) {
      toast({
        title: "Welcome back!",
        description: "Login successful"
      });
      navigate('/admin');
    } else {
      toast({
        title: "Login failed",
        description: result.error,
        variant: "destructive"
      });
    }
    
    setLoading(false);
  };

  const handleSetupSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!setupData.username || !setupData.email || !setupData.password) {
      toast({
        title: "Error",
        description: "Please fill in all fields",
        variant: "destructive"
      });
      return;
    }

    if (setupData.password.length < 8) {
      toast({
        title: "Error",
        description: "Password must be at least 8 characters",
        variant: "destructive"
      });
      return;
    }

    if (setupData.password !== setupData.confirmPassword) {
      toast({
        title: "Error",
        description: "Passwords do not match",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/auth/setup`, {
        username: setupData.username,
        email: setupData.email,
        password: setupData.password
      });

      toast({
        title: "Success!",
        description: "Admin account created. Please login."
      });
      
      setIsSetupMode(false);
      setLoginData({
        username: setupData.username,
        password: ''
      });
    } catch (error) {
      toast({
        title: "Setup failed",
        description: error.response?.data?.detail || "Failed to create admin account",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  if (checkingSetup) {
    return (
      <div className="login-container">
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="login-container">
      <div className="login-content">
        {/* Logo and Brand */}
        <div className="login-brand">
          <Leaf size={48} className="brand-icon" />
          <h1 className="brand-title">The LaShay & Silk Collective</h1>
          <p className="brand-subtitle">Admin Dashboard</p>
        </div>

        {/* Login/Setup Card */}
        <Card className="login-card">
          <CardContent className="login-card-content">
            {isSetupMode ? (
              <>
                <div className="form-header">
                  <h2 className="form-title">Create Admin Account</h2>
                  <p className="form-description">
                    Set up your admin account to manage your products
                  </p>
                </div>

                <form onSubmit={handleSetupSubmit} className="login-form">
                  <div className="form-group">
                    <Label htmlFor="setup-username">Username *</Label>
                    <div className="input-wrapper">
                      <User className="input-icon" size={18} />
                      <Input
                        id="setup-username"
                        type="text"
                        placeholder="Choose a username"
                        value={setupData.username}
                        onChange={(e) => setSetupData({ ...setupData, username: e.target.value })}
                        className="input-with-icon"
                        required
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <Label htmlFor="setup-email">Email *</Label>
                    <div className="input-wrapper">
                      <Mail className="input-icon" size={18} />
                      <Input
                        id="setup-email"
                        type="email"
                        placeholder="your@email.com"
                        value={setupData.email}
                        onChange={(e) => setSetupData({ ...setupData, email: e.target.value })}
                        className="input-with-icon"
                        required
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <Label htmlFor="setup-password">Password *</Label>
                    <div className="input-wrapper">
                      <Lock className="input-icon" size={18} />
                      <Input
                        id="setup-password"
                        type="password"
                        placeholder="At least 8 characters"
                        value={setupData.password}
                        onChange={(e) => setSetupData({ ...setupData, password: e.target.value })}
                        className="input-with-icon"
                        required
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <Label htmlFor="setup-confirm-password">Confirm Password *</Label>
                    <div className="input-wrapper">
                      <Lock className="input-icon" size={18} />
                      <Input
                        id="setup-confirm-password"
                        type="password"
                        placeholder="Confirm your password"
                        value={setupData.confirmPassword}
                        onChange={(e) => setSetupData({ ...setupData, confirmPassword: e.target.value })}
                        className="input-with-icon"
                        required
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    className="login-button"
                    disabled={loading}
                  >
                    {loading ? 'Creating Account...' : 'Create Admin Account'}
                  </Button>
                </form>
              </>
            ) : (
              <>
                <div className="form-header">
                  <LogIn size={40} className="form-icon" />
                  <h2 className="form-title">Admin Login</h2>
                  <p className="form-description">
                    Sign in to access your dashboard
                  </p>
                </div>

                <form onSubmit={handleLoginSubmit} className="login-form">
                  <div className="form-group">
                    <Label htmlFor="username">Username</Label>
                    <div className="input-wrapper">
                      <User className="input-icon" size={18} />
                      <Input
                        id="username"
                        type="text"
                        placeholder="Enter your username"
                        value={loginData.username}
                        onChange={(e) => setLoginData({ ...loginData, username: e.target.value })}
                        className="input-with-icon"
                        required
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <Label htmlFor="password">Password</Label>
                    <div className="input-wrapper">
                      <Lock className="input-icon" size={18} />
                      <Input
                        id="password"
                        type="password"
                        placeholder="Enter your password"
                        value={loginData.password}
                        onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                        className="input-with-icon"
                        required
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    className="login-button"
                    disabled={loading}
                  >
                    <LogIn size={18} className="mr-2" />
                    {loading ? 'Signing in...' : 'Sign In'}
                  </Button>
                </form>
              </>
            )}
          </CardContent>
        </Card>

        {/* Back to Site Link */}
        <div className="login-footer">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="back-to-site-btn"
          >
            ← Back to Website
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
